/**
 * Help article registry + search — R-131-SUP-C
 *
 * Markdown lives in docs/help/*.md (version-controlled; editable via PR).
 * Articles are bundled at build time via Vite ?raw imports.
 */
// R-131-SUP-C
import { parse as parseYaml } from "yaml";

import gettingStartedMd from "../../docs/help/getting-started.md?raw";
import sitesMd from "../../docs/help/sites.md?raw";
import reportsMd from "../../docs/help/reports.md?raw";
import auditFindingsMd from "../../docs/help/audit-findings.md?raw";
import pricingMd from "../../docs/help/pricing.md?raw";
import faqMd from "../../docs/help/faq.md?raw";
import financialAssumptionsMd from "../../docs/help/financial-assumptions.md?raw";
import buildingBasicSimulationNetworksMd from "../../docs/help/building-basic-simulation-networks.md?raw";

/** Index sections shown on /help — order is display order. */
export const HELP_SECTIONS = [
  "Getting Started",
  "Simulation Workbench",
  "Creating Sites",
  // Keep this synchronized with docs/help/reports.md frontmatter. The constant MUST
  // track the frontmatter — `parseArticle` falls back to 'FAQ' for an unknown
  // section, so a drift here silently misfiles the article on /help instead of
  // failing loudly.
  "Analyses and Decision Outputs",
  "Understanding Audit Findings",
  "Pricing",
  "FAQ",
] as const;

export type HelpSection = (typeof HELP_SECTIONS)[number];

export type HelpSlug =
  | "getting-started"
  | "sites"
  | "reports"
  | "audit-findings"
  | "pricing"
  | "faq"
  | "financial-assumptions"
  | "building-basic-simulation-networks";

export interface HelpArticle {
  slug: HelpSlug;
  title: string;
  section: HelpSection;
  description: string;
  raw: string;
  body: string;
  headings: string[];
}

export interface HelpSearchHit {
  slug: HelpSlug;
  title: string;
  section: HelpSection;
  /** Matching snippet — article title or a heading line. */
  matchLabel: string;
  matchKind: "title" | "heading";
}

const RAW_BY_SLUG: Record<HelpSlug, string> = {
  "getting-started": gettingStartedMd,
  sites: sitesMd,
  reports: reportsMd,
  "audit-findings": auditFindingsMd,
  pricing: pricingMd,
  faq: faqMd,
  "financial-assumptions": financialAssumptionsMd,
  "building-basic-simulation-networks": buildingBasicSimulationNetworksMd,
};

export const HELP_SLUGS = Object.keys(RAW_BY_SLUG) as HelpSlug[];

function stripFrontmatter(raw: string): {
  body: string;
  meta: Record<string, string>;
} {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/.exec(raw);
  if (!match) return { body: raw, meta: {} };
  try {
    const parsed = parseYaml(match[1]);
    const meta: Record<string, string> = {};
    if (parsed && typeof parsed === "object") {
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        meta[k] = String(v);
      }
    }
    return { body: match[2] ?? "", meta };
  } catch {
    return { body: match[2] ?? raw, meta: {} };
  }
}

export function extractHeadings(markdownBody: string): string[] {
  const headings: string[] = [];
  for (const line of markdownBody.split("\n")) {
    const m = /^(#{1,6})\s+(.+)$/.exec(line.trim());
    if (m?.[2]) {
      headings.push(m[2].replace(/[#*`]/g, "").trim());
    }
  }
  return headings;
}

function parseArticle(slug: HelpSlug, raw: string): HelpArticle {
  const { body, meta } = stripFrontmatter(raw);
  const section = (meta.section ?? "FAQ") as HelpSection;
  return {
    slug,
    title: meta.title ?? slug,
    section: HELP_SECTIONS.includes(section as HelpSection)
      ? (section as HelpSection)
      : "FAQ",
    description: meta.description ?? "",
    raw,
    body,
    headings: extractHeadings(body),
  };
}

export const HELP_ARTICLES: HelpArticle[] = HELP_SLUGS.map((slug) =>
  parseArticle(slug, RAW_BY_SLUG[slug]),
);

export const HELP_ARTICLE_BY_SLUG: Record<HelpSlug, HelpArticle> =
  Object.fromEntries(HELP_ARTICLES.map((a) => [a.slug, a])) as Record<
    HelpSlug,
    HelpArticle
  >;

export function isHelpSlug(value: string | undefined): value is HelpSlug {
  return !!value && value in RAW_BY_SLUG;
}

export function articlesBySection(section: HelpSection): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.section === section);
}

/**
 * Substring fuzzy search across article titles + headings.
 * Empty query returns all articles as title hits (browse mode).
 */
export function searchHelpArticles(query: string): HelpSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return HELP_ARTICLES.map((a) => ({
      slug: a.slug,
      title: a.title,
      section: a.section,
      matchLabel: a.title,
      matchKind: "title" as const,
    }));
  }

  const hits: HelpSearchHit[] = [];
  for (const article of HELP_ARTICLES) {
    if (article.title.toLowerCase().includes(q)) {
      hits.push({
        slug: article.slug,
        title: article.title,
        section: article.section,
        matchLabel: article.title,
        matchKind: "title",
      });
    }
    for (const heading of article.headings) {
      if (heading.toLowerCase().includes(q)) {
        hits.push({
          slug: article.slug,
          title: article.title,
          section: article.section,
          matchLabel: heading,
          matchKind: "heading",
        });
      }
    }
  }
  return hits;
}

export function helpArticlePath(slug: HelpSlug): string {
  return `/help/${slug}`;
}
